import { i18n } from '../i18n.js';
/**
 * Consolidates terminal-saturation feedback into the storyboard.
 * One match/turn key owns both the text entry and the enemy emoji reaction.
 */


import { StoryFeed } from './story-feed.js';
import { ThinkingIndicator } from './thinking-indicator.js';
let DeckoutFeedback = {};

DeckoutFeedback = (function () {
    'use strict';

    const messagesByTurn = new Map();
    const reactedTurns = new Set();

    function translate(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        return translated && translated !== key ? translated : fallback;
    }

    function turnKey(matchId, turn) {
        if (typeof matchId !== 'string' || !matchId.trim()) return null;
        if (!Number.isInteger(turn) || turn < 0) return null;
        return `deckout:${matchId.trim()}:${turn}`;
    }

    function sameMatch(first, second) {
        return Boolean(
            first?.roomId
            && first?.matchId
            && first.roomId === second?.roomId
            && first.matchId === second?.matchId
        );
    }

    function showFeedback({ matchId, turn, message, reaction }) {
        const key = turnKey(matchId, turn);
        if (!key || !message) return false;

        const storyFeed = StoryFeed;
        if (messagesByTurn.get(key) !== message) {
            if (typeof storyFeed?.upsertSystemEntry !== 'function') return false;
            storyFeed.upsertSystemEntry(message, {
                key,
                kind: 'deckout',
                turn,
                announce: true,
            });
            messagesByTurn.set(key, message);
        }

        if (!reactedTurns.has(key)) {
            const shown = ThinkingIndicator?.showReaction?.('opponent', reaction);
            if (shown !== false) reactedTurns.add(key);
        }
        return true;
    }

    function handleOverflow(payload, currentView) {
        if (!sameMatch(payload, currentView) || !payload?.playerId) return false;
        const isPlayer = payload.playerId === currentView?.player?.id;
        const isOpponent = payload.playerId === currentView?.opponent?.id;
        if (!isPlayer && !isOpponent) return false;

        let message;
        if (isPlayer) {
            const cardName = typeof payload.discardedCardName === 'string'
                ? payload.discardedCardName.trim()
                : '';
            if (!cardName) return false;
            message = translate(
                'game.turnDrawOverflowOwner',
                `Your full hand and battlefield burned ${cardName} from your deck.`,
                { cardName },
            );
        } else {
            message = translate(
                'game.turnDrawOverflowOpponent',
                "Your opponent's full hand and battlefield burned the top card of their deck.",
            );
        }

        return showFeedback({
            matchId: payload.matchId,
            turn: payload.turn,
            message,
            reaction: isPlayer ? 'deckoutGloat' : 'deckoutAlarm',
        });
    }

    function strikeWarning(strikes) {
        if (strikes === 1) {
            return translate(
                'game.deckoutStrikesTwoRemaining',
                'Deckout strike 1/3 — two more mean defeat.',
            );
        }
        if (strikes === 2) {
            return translate(
                'game.deckoutStrikesOneRemaining',
                'Deckout strikes 2/3 — one more means defeat.',
            );
        }
        if (strikes === 3) {
            return translate(
                'game.deckoutStrikesFinal',
                'Deckout strike 3/3 — the match ends.',
            );
        }
        return '';
    }

    function increasedStrike(previousView, nextView) {
        for (const side of ['player', 'opponent']) {
            const previous = Number(previousView?.[side]?.deckoutStrikes) || 0;
            const next = Number(nextView?.[side]?.deckoutStrikes) || 0;
            if (Number.isInteger(next) && next > previous && next <= 3) {
                return { side, strikes: next };
            }
        }
        return null;
    }

    function handleStateUpdate(previousView, nextView) {
        if (!sameMatch(previousView, nextView)) return false;
        const increment = increasedStrike(previousView, nextView);
        if (!increment) return false;
        const warning = strikeWarning(increment.strikes);
        if (!warning) return false;

        const isPlayer = increment.side === 'player';
        const message = isPlayer
            ? translate(
                'game.deckoutStoryboardPlayer',
                `Your deck — ${warning}`,
                { warning },
            )
            : translate(
                'game.deckoutStoryboardOpponent',
                `Opponent's deck — ${warning}`,
                { warning },
            );

        return showFeedback({
            matchId: nextView.matchId,
            turn: nextView.currentTurn,
            message,
            reaction: isPlayer ? 'deckoutGloat' : 'deckoutAlarm',
        });
    }

    function reset() {
        messagesByTurn.clear();
        reactedTurns.clear();
    }

    return { handleOverflow, handleStateUpdate, reset };
})();

export { DeckoutFeedback };
